import { useEffect, useRef, useState } from "react";
import { useLocales } from "../../hooks/LocalesContext";
import { MenuItem, Select, SelectChangeEvent, FormControl, InputLabel, Box, Typography, SxProps, Theme } from "@mui/material";
import { LocalModel } from "../../models/LocalModel";

interface LocalSelectorProps {
    onLocalChange: (idLocal: string | undefined) => void;
    layout?: 'horizontal' | 'vertical'; // Optional layout parameter
    label?: string; // Optional custom label
    size?: 'small' | 'medium';
    sx?: SxProps<Theme>;
    selectSx?: SxProps<Theme>;
    localesPermitidos?: LocalModel[];
    ocultarSiHayUno?: boolean;
    valorInicial?: string;
}

const LocalSelector: React.FC<LocalSelectorProps> = ({ 
    onLocalChange, 
    layout = 'vertical', 
    label = 'Local',
    size = 'medium',
    sx,
    selectSx,
    localesPermitidos,
    ocultarSiHayUno = false,
    valorInicial
}) => {
    const { locales } = useLocales();
    const localesDisponibles = localesPermitidos ?? locales;
    const [selectedLocal, setSelectedLocal] = useState<string>(valorInicial ?? "");
    const onLocalChangeRef = useRef(onLocalChange);
    const lastNotifiedRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        onLocalChangeRef.current = onLocalChange;
    }, [onLocalChange]);

    useEffect(() => {
        const notify = (value: string | undefined) => {
            if (lastNotifiedRef.current !== value) {
                lastNotifiedRef.current = value;
                onLocalChangeRef.current(value);
            }
        };

        if (localesDisponibles.length > 0) {
            const currentExists = localesDisponibles.some((local) => local.idLocal === selectedLocal);
            const nextLocal = currentExists ? selectedLocal : localesDisponibles[0].idLocal;
            if (nextLocal !== selectedLocal) {
                setSelectedLocal(nextLocal);
            }
            notify(nextLocal);
        } else {
            setSelectedLocal("");
            notify(undefined);
        }
    }, [localesDisponibles, selectedLocal]);

    const handleChange = (event: SelectChangeEvent) => {
        lastNotifiedRef.current = event.target.value;
        onLocalChange(event.target.value);
        setSelectedLocal(event.target.value);
    };

    if (ocultarSiHayUno && localesDisponibles.length === 1) return null;

    return (
        <>
            {localesDisponibles && (
                layout === 'vertical' ? (
                    <FormControl fullWidth size={size} sx={sx}>
                        <InputLabel id="local-select-label">{label}</InputLabel>
                        <Select
                            labelId="local-select-label"
                            id="local-select"
                            value={selectedLocal}
                            label={label}
                            onChange={handleChange}
                            sx={selectSx}
                        >
                            {localesDisponibles.map(l => (
                                <MenuItem key={l.idLocal} value={l.idLocal}>{l.fantasia}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography>{label}:</Typography>
                        <Select
                            labelId="local-select-label-horizontal"
                            id="local-select-horizontal"
                            value={selectedLocal}
                            onChange={handleChange}
                            variant="outlined"
                            size={size}
                            sx={selectSx}
                        >
                            {localesDisponibles.map(l => (
                                <MenuItem key={l.idLocal} value={l.idLocal}>{l.fantasia}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                )
            )}
        </>
    );
}

export default LocalSelector;
